import * as signalR from '@microsoft/signalr';

/**
 * Service to handle SignalR connections for real-time updates
 */
interface PendingAction {
  key: string;
  action: () => Promise<void>;
}

export class SignalRService {
  private static connection: signalR.HubConnection | null = null;
  private static connectionPromise: Promise<void> | null = null;
  private static listeners: Record<string, Array<(...args: any[]) => void>> = {};
  private static currentThreadId: number | null = null;
  private static pendingActions: Map<string, PendingAction> = new Map();
  private static isConnected: boolean = false;

  /**
   * Initialize the SignalR connection
   */
  static async initializeConnection(): Promise<void> {
    if (this.connection) {
      return;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/notifications')
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Set up connection event handlers
    this.connection.onreconnecting(error => {
      console.log('SignalR reconnecting due to error:', error);
    });

    this.connection.onreconnected(connectionId => {
      console.log('SignalR reconnected with ID:', connectionId);
      this.isConnected = true;
      this.reattachListeners();
      this.executePendingActions();
    });

    this.connection.onclose(error => {
      console.log('SignalR connection closed due to error:', error);
    });

    // Start the connection
    this.connectionPromise = this.connection.start()
      .then(() => {
        console.log('SignalR connection established');
        this.isConnected = true;
        this.reattachListeners();
        this.executePendingActions();
      })
      .catch(err => {
        console.error('Error establishing SignalR connection:', err);
        this.connection = null;
        this.connectionPromise = null;
        this.isConnected = false;
        throw err;
      });

    return this.connectionPromise;
  }

  /**
   * Add a listener for a specific event
   */
  static addListener(eventName: string, callback: (...args: any[]) => void): void {
    // Initialize listeners array for this event if needed
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }

    // Add the callback to the listeners
    this.listeners[eventName].push(callback);

    // Attach the listener if connection exists
    if (this.connection) {
      this.connection.on(eventName, callback);
    }
  }

  /**
   * Remove a listener for a specific event
   */
  static removeListener(eventName: string, callback: (...args: any[]) => void): void {
    if (!this.listeners[eventName]) {
      return;
    }

    // Remove the callback from our listeners array
    this.listeners[eventName] = this.listeners[eventName].filter(cb => cb !== callback);

    // Remove from the SignalR connection if it exists
    if (this.connection) {
      this.connection.off(eventName, callback);
    }
  }

  /**
   * Reattach all listeners after reconnection
   */
  private static reattachListeners(): void {
    if (!this.connection) return;

    // Reattach all listeners
    Object.entries(this.listeners).forEach(([eventName, callbacks]) => {
      callbacks.forEach(callback => {
        this.connection!.on(eventName, callback);
      });
    });
  }

  /**
   * Execute pending actions once connected
   */
  private static async executePendingActions(): Promise<void> {
    if (!this.isConnected || !this.connection) return;

    const actions = Array.from(this.pendingActions.values());
    this.pendingActions.clear();

    for (const pendingAction of actions) {
      await pendingAction.action();
    }
  }

  /**
   * Execute action now if connected, or queue it for when we connect
   */
  private static async executeWhenConnected(key: string, action: () => Promise<void>): Promise<void> {
    if (this.isConnected && this.connection) {
      await action();
    } else {
      this.pendingActions.set(key, { key, action });
      await this.initializeConnection();
    }
  }

  /**
   * Join a thread group for real-time updates
   */
  static async joinThread(threadId: number): Promise<void> {
    this.currentThreadId = threadId;
    
    await this.executeWhenConnected(`joinThread-${threadId}`, async () => {
      if (this.connection) {
        await this.connection.invoke('JoinThread', threadId);
      }
    });
  }

  /**
   * Leave the current thread group
   */
  static async leaveThread(threadId: number): Promise<void> {
    if (this.currentThreadId === threadId) {
      this.currentThreadId = null;
    }
    
    // Remove any pending join actions for this thread
    this.pendingActions.delete(`joinThread-${threadId}`);
    
    await this.executeWhenConnected(`leaveThread-${threadId}`, async () => {
      if (this.connection) {
        await this.connection.invoke('LeaveThread', threadId);
      }
    });
  }

  /**
   * Send typing indicator to other users in the thread
   */
  static async startTyping(threadId: number): Promise<void> {
    await this.executeWhenConnected(`startTyping-${threadId}`, async () => {
      if (this.connection) {
        await this.connection.invoke('StartTyping', threadId);
      }
    });
  }

  /**
   * Stop typing indicator
   */
  static async stopTyping(threadId: number): Promise<void> {
    // Remove any pending start typing actions for this thread
    this.pendingActions.delete(`startTyping-${threadId}`);
    
    await this.executeWhenConnected(`stopTyping-${threadId}`, async () => {
      if (this.connection) {
        await this.connection.invoke('StopTyping', threadId);
      }
    });
  }

  /**
   * Get user presence information
   */
  static async getUserPresence(userId: string): Promise<void> {
    await this.executeWhenConnected(`getUserPresence-${userId}`, async () => {
      if (this.connection) {
        await this.connection.invoke('GetUserPresence', userId);
      }
    });
  }

  /**
   * Get the current thread ID
   */
  static getCurrentThreadId(): number | null {
    return this.currentThreadId;
  }

  /**
   * Close the SignalR connection
   */
  static async closeConnection(): Promise<void> {
    if (this.connection) {
      // Leave current thread before closing
      if (this.currentThreadId) {
        await this.leaveThread(this.currentThreadId);
      }
      
      await this.connection.stop();
      this.connection = null;
      this.connectionPromise = null;
      this.currentThreadId = null;
      this.isConnected = false;
      this.pendingActions.clear();
    }
  }
}